/**
 * @file    loader.ts
 * @scope   SHARED
 * @summary Loads a persisted widget: reads the envelope, dispatches to the matching subtype
 *          delta by `chartTypeId`, migrates older versions and flags newer ones as read-only.
 * @consumes readEnvelope, deserializeWithDelta, SerializerDelta
 * @produces LoadResult, createWidgetLoader
 * @sideEffects None.
 * @notes   Unknown `chartTypeId` (or a subtype without delta) returns an `unknown` result — a Null
 *          Object the caller can ignore or render a safe placeholder for — instead of throwing.
 *          The engine stays registry-agnostic: descriptors are injected (DIP).
 */
import { CustomConfig } from 'models/WidgetModel';
import type { SerializerDelta } from '../contracts';
import { deserializeWithDelta } from './baseSerializer';
import { readEnvelope } from './envelope';

export type LoadResult<TSlice> =
  | {
      status: 'loaded';
      chartTypeId: string;
      slice: TSlice;
      /** WHY: payload newer than this client → never re-save and downgrade it (§7.1). */
      readOnly: boolean;
    }
  | { status: 'unknown'; chartTypeId: string };

export type LoadableDescriptor<TSlice> = {
  id: string;
  featureFlag?: string;
  serializerDelta?: SerializerDelta<TSlice>;
};

/**
 * @param descriptors Registered subtypes.
 * @param defaultChartTypeId Subtype for configs saved before the envelope existed.
 * @returns A pure `(config, isFlagEnabled?) => LoadResult` function. A gated-off subtype loads
 *          as `unknown` (Phase 6); omitting the predicate keeps every subtype enabled.
 */
export const createWidgetLoader =
  <TSlice>(descriptors: LoadableDescriptor<TSlice>[], defaultChartTypeId: string) =>
  (
    config: CustomConfig | undefined,
    isFlagEnabled: (flag?: string) => boolean = () => true
  ): LoadResult<TSlice> => {
    const { chartTypeId, version } = readEnvelope(
      config,
      defaultChartTypeId,
      descriptors.map((d) => d.id)
    );
    const descriptor = descriptors.find((d) => d.id === chartTypeId);
    const delta = descriptor?.serializerDelta;

    if (!descriptor || !delta || !isFlagEnabled(descriptor.featureFlag)) {
      return { status: 'unknown', chartTypeId };
    }

    const source =
      version < delta.version && delta.migrate
        ? (delta.migrate(config, version) as CustomConfig | undefined)
        : config;

    return {
      status: 'loaded',
      chartTypeId,
      slice: deserializeWithDelta(source, delta),
      readOnly: version > delta.version,
    };
  };
